import axios from 'axios';
import { fetchTableItems } from '.';
import {
	AppBanner,
	AppBannerDto,
	Contact,
	TableItemsOptions,
	Partner,
	ExternalOrganisation,
	AddPartner,
	ViewPartner,
	Center,
	UserAccount,
	CaseNote,
	ContactProfileCaseNoteDto,
} from '../../types';
import { ContactSearchCondition } from '../../types/contact';

export interface ContactBulkSearchCondition {
	conditions: ContactSearchCondition[];
	exact?: boolean;
}

export const contactApi = {
	async bulkSearchContacts(
		conditions: ContactBulkSearchCondition[],
	): Promise<Contact[][]> {
		const { data } = await axios.post(
			`/contact/contacts/bulk-search`,
			conditions,
		);
		return data;
	},

	async searchUnrelatedContacts(
		forContactId: number,
		conditions: ContactSearchCondition[],
	): Promise<Contact[]> {
		const { data } = await axios.post(
			`/contact/contacts/${forContactId}/search-unrelated`,
			{
				conditions,
			},
		);
		return data;
	},

	async fetchAppBannerItems(): Promise<any> {
		const { data } = await axios.get(`/contact/ngo-banners`);
		return data;
	},

	async reorderAppBannerItems(ids: string[]) {
		const { data } = await axios.post(`/contact/ngo-banners/reorder`, ids);
		return data;
	},

	async createAppBannerItem(appBannerDto: AppBannerDto): Promise<AppBanner> {
		const { data } = await axios.post(`/contact/ngo-banners`, appBannerDto);
		return data;
	},

	async updateAppBannerItem(
		id: string,
		appBannerDto: AppBannerDto,
	): Promise<AppBanner> {
		const { data } = await axios.put(
			`/contact/ngo-banners/${id}`,
			appBannerDto,
		);
		return data;
	},

	async deleteAppBannerItem(id: string): Promise<AppBanner> {
		const { data } = await axios.delete(`/contact/ngo-banners/${id}`);
		return data;
	},

	async fetchOwnerCentersForProfile(profileId: number): Promise<Center[]> {
		const { data } = await axios.get(
			`/contact/contact-profiles/${profileId}/available-owners`,
		);
		return data;
	},

	async fetchAvailableUsersForProfile(
		profileId: number,
	): Promise<UserAccount[]> {
		const { data } = await axios.get(
			`/contact/contact-profiles/${profileId}/available-users`,
		);
		return data;
	},

	async fetchPartnersItems(
		params: TableItemsOptions,
	): Promise<{ result: ViewPartner[]; total: string }> {
		const url = '/contact/partners';
		const data = fetchTableItems({ ...params, url: url });
		return data;
	},

	async fetchPartners(withDeleted?: boolean): Promise<ViewPartner[]> {
		const { data } = await axios.get('/contact/partners', {
			params: { withDeleted },
		});
		return data;
	},

	async fetchNgoForPartnershipItems(
		options: TableItemsOptions,
	): Promise<{ result: AddPartner[]; total: string }> {
		// const url = `/contact/ngos?approvalStatuses[]=${NgoApprovalStatus.approved}&filterPartners=true`;
		const url = `/contact/add-partners`;
		const data = fetchTableItems({ ...options, url: url });
		return data;
	},

	async deletePartner(id: string): Promise<Partner> {
		const { data } = await axios.delete(`/contact/partners/${id}`);
		return data;
	},

	async addPartners(
		ngoId: number,
		partnerNgoIds: number[],
		partnerExtOrgIds: string[],
	): Promise<Partner> {
		const { data } = await axios.post(`/contact/partners`, {
			ngoId,
			partnerNgoIds,
			partnerExtOrgIds,
		});
		return data;
	},

	async getExternalOrganisation(id: string): Promise<ExternalOrganisation> {
		const { data } = await axios.get(`/contact/external-organisations/${id}`);
		return data;
	},

	async addExternalOrganisation(
		extOrg: Partial<ExternalOrganisation>,
	): Promise<ExternalOrganisation> {
		const { data } = await axios.post(
			`/contact/external-organisations`,
			extOrg,
		);
		return data;
	},

	async updateExternalOrganisation(
		extOrg: Partial<ExternalOrganisation>,
	): Promise<ExternalOrganisation> {
		const { data } = await axios.put(
			`/contact/external-organisations/${extOrg.id}`,
			extOrg,
		);
		return data;
	},

	async getQrCode(id: string) {
		const { data } = await axios.get(`/contact/qr-codes/${id}`);
		return data;
	},

	async fetchContactProfileCaseNotes(profileId: number): Promise<CaseNote> {
		const { data } = await axios.get(
			`/contact/contact-profiles/${profileId}/case-notes`,
		);
		return data;
	},

	async fetchContactProfileCaseNote(
		profileId: number,
		noteId: string,
	): Promise<CaseNote> {
		const { data } = await axios.get(
			`/contact/contact-profiles/${profileId}/case-notes/${noteId}`,
		);
		return data;
	},

	async createContactProfileCaseNote(
		profileId: number,
		dto: ContactProfileCaseNoteDto,
	): Promise<CaseNote> {
		const { data } = await axios.post(
			`/contact/contact-profiles/${profileId}/case-notes`,
			dto,
		);
		return data;
	},

	async updateContactProfileCaseNote(
		profileId: number,
		noteId: string,
		dto: ContactProfileCaseNoteDto,
	): Promise<CaseNote> {
		const { data } = await axios.put(
			`/contact/contact-profiles/${profileId}/case-notes/${noteId}`,
			dto,
		);
		return data;
	},
};
